import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import {
  addDoc,
  collection,
  doc,
  setDoc,
  writeBatch,
} from "firebase/firestore";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { db, auth } from "../@shared/firebase/config"; // Update your paths
import { useCardStore } from "../@shared/store/useCardsStore";

export default function CreateTestAccountModal({ open, onClose }) {
  const { characterCards, infinityCards } = useCardStore();
  const [username, setUsername] = useState("");
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!username || selectedCharacters.length !== 3) return;

    const email = `${username}@infinftroyale.com`;
    const defaultData = {
      username,
      name: "Play Test",
      email,
      roles: ["player"],
      record: { wins: 0, losses: 0, ties: 0, dnfs: 0 },
      imageUrl: "",
      world: "Void City",
      avatar:
        "https://firebasestorage.googleapis.com/v0/b/infinft-card-game.firebasestorage.app/o/avatars%2Fgreen.png?alt=media&token=b504236c-a42f-4a2c-a2bd-9742b489a398",
      xp: 0,
      level: 1,
      emailVerified: false,
      completedTutorial: false,
      test: true,
      lastLogin: new Date(),
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    try {
      setLoading(true);

      // Step 1: Create Firebase Auth user
      const userCred = await createUserWithEmailAndPassword(
        auth,
        email,
        "password"
      );
      const userId = userCred.user.uid;

      // Step 2: Create user doc
      const userRef = doc(db, "users", userId);
      await setDoc(userRef, defaultData);

      // Step 3: Batch write character cards
      const batch = writeBatch(db);
      selectedCharacters.forEach((card) => {
        const charCardRef = doc(db, `users/${userId}/Character cards`, card.id);
        batch.set(charCardRef, { ...card, amountOwned: 1 });
      });

      // Step 4: Add 40 random infinity cards
      const shuffled = [...infinityCards]
        .sort(() => 0.5 - Math.random())
        .slice(0, 40);
      shuffled.forEach((card, idx) => {
        const infRef = doc(db, `users/${userId}/Infinity cards`, `card_${idx}`);
        batch.set(infRef, { ...card });
      });

      // Step 5: Create deck with same 40 cards
      const deckRef = doc(collection(db, `users/${userId}/decks`));
      batch.set(deckRef, {
        cards: shuffled,
        color: "#0073ff",
        createdAt: new Date(),
        updatedAt: new Date(),
        name: "Test Deck",
      });

      await batch.commit();
      console.log("✅ Test account created");

      // Reset and close
      setUsername("");
      setSelectedCharacters([]);
      onClose();
    } catch (err) {
      console.error("❌ Failed to create test user:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create Test Account</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="normal"
          label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <Autocomplete
          multiple
          options={characterCards}
          getOptionLabel={(option) => option.name}
          value={selectedCharacters}
          onChange={(_, newValue) => setSelectedCharacters(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select 3 Character Cards"
              margin="normal"
              helperText={`${selectedCharacters.length} / 3 selected`}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          disabled={selectedCharacters.length !== 3 || !username || loading}
          variant="contained"
        >
          {loading ? <CircularProgress size={20} /> : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
